import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, type TranslateFunction, useTranslation } from 'locales/index';
import { useMemo } from 'react';
import { entries, isEmpty } from 'remeda';
import type { CustomVariable } from 'shared/hooks/use-load-company-configuration';
import { useGetConstants } from 'shared/utils/selectors';
import type { TemplateVariables } from 'types/store/app-state';

export type TemplateVariableItem = {
  value: string;
  description: string;
};

export type TemplateVariableCategory = {
  label: string;
  variables: TemplateVariableItem[];
};

const buildCategories = (
  perStep: TemplateVariables | undefined,
  otherLabel: string,
): TemplateVariableCategory[] => {
  if (!perStep) return [];

  const labelledVariables = Object.values(perStep).flatMap(groups =>
    groups.flatMap(entry =>
      'variables' in entry
        ? entry.variables.map(({ value, description }) => ({
            label: entry.description,
            variable: { value, description },
          }))
        : [{ label: otherLabel, variable: { value: entry.value, description: entry.description } }],
    ),
  );

  const variablesByLabel = new Map<string, Map<string, TemplateVariableItem>>();

  labelledVariables.forEach(({ label, variable }) => {
    const items = variablesByLabel.get(label) ?? new Map<string, TemplateVariableItem>();
    if (!items.has(variable.value)) items.set(variable.value, variable);
    variablesByLabel.set(label, items);
  });

  return [...variablesByLabel.entries()].map(([label, items]) => ({
    label,
    variables: [...items.values()],
  }));
};

// The backend resolves a custom variable as `{{<model_type>_<column_name>}}`, see custom_var_hash.
const CUSTOM_VARIABLE_CATEGORY_LABEL_KEYS = {
  debtor: i18nKeys.AUTOMATION.WIZARD.DEBTOR_CUSTOM_VARIABLES_GROUP,
  invoice: i18nKeys.AUTOMATION.WIZARD.INVOICE_CUSTOM_VARIABLES_GROUP,
} as const;

const buildCustomVariableCategories = (
  customVariables: ReadonlyArray<CustomVariable>,
  t: TranslateFunction,
): TemplateVariableCategory[] =>
  entries(CUSTOM_VARIABLE_CATEGORY_LABEL_KEYS).flatMap(([modelType, labelKey]) => {
    const variables = customVariables
      .filter(customVariable => customVariable.model_type === modelType)
      .map(({ name, model_type, column_name }) => ({
        value: `${model_type}_${column_name}`,
        description: name,
      }));

    return isEmpty(variables) ? [] : [{ label: t(labelKey), variables }];
  });

export const useTemplateVariableCategories = (planType: string): TemplateVariableCategory[] => {
  const { t } = useTranslation();
  const constants = useGetConstants();
  const { custom_variables: customVariables } = useCompanyConfiguration();

  return useMemo(() => {
    const perStep =
      planType === 'payment'
        ? constants?.payment_plan_template_variables
        : constants?.recovery_plan_template_variables;

    return [
      ...buildCategories(perStep, t(i18nKeys.OTHER)),
      ...buildCustomVariableCategories(customVariables, t),
    ];
  }, [constants, customVariables, planType, t]);
};

export const useGetVariableCategoryLabel = (categories: TemplateVariableCategory[]) =>
  useMemo(() => {
    const categoryLabels = new Map(
      categories.flatMap(({ label, variables }) =>
        variables.map(({ value }) => [value, label] as const),
      ),
    );

    return (value: string) => categoryLabels.get(value) ?? null;
  }, [categories]);

export const useGetVariableLabel = (categories: TemplateVariableCategory[]) =>
  useMemo(() => {
    const labels = new Map(
      categories.flatMap(({ variables }) =>
        variables.map(({ value, description }) => [value, description] as const),
      ),
    );

    return (value: string) => labels.get(value) ?? value;
  }, [categories]);
